import { useCallback, useEffect, useState } from 'react'
import { type DateRange, DayPicker } from 'react-day-picker'

import { FvButton } from '@fv/client-components'
import { parseDateRange } from '@fv/client-core'
import { fvDate } from '@fv/dates'

import {
  getUtcDayRangeParam,
  hasCompleteDateRange,
  toDatePickerRange,
} from '@/features/dates/dateRange'

import { Pinline } from '../Pinline'
import { ShipperPopup } from '../ShipperPopup'
import { SelectFilter } from './SelectFilter'
import type { AnyFilterComponentProps } from './types'

const dateFormat = 'MMM DD, YYYY'

export const DateFilter = ({
  name,
  options,
  setQueryString,
  value: rawValue,
  openDatePicker,
  setOpenDatePicker,
}: AnyFilterComponentProps) => {
  const value = typeof rawValue === 'string' ? rawValue : (rawValue[0] ?? '')
  const isDatePickerOpen = openDatePicker === name
  const { start, end, isCustom, name: dateRangeName } = parseDateRange(value)
  const selectedRange = toDatePickerRange(start, end)
  const [localRange, setLocalRange] = useState<DateRange | undefined>({
    from: selectedRange.from,
    to: selectedRange.to,
  })
  const customOption = {
    text: 'Custom',
    value: 'custom',
  }

  useEffect(() => {
    if (isCustom && !start && !end && !isDatePickerOpen) {
      setLocalRange(undefined)
      setOpenDatePicker?.(name)
    }
  }, [isCustom, start, end, isDatePickerOpen, name, setOpenDatePicker])

  const setDateRange = useCallback(
    (range?: DateRange) => {
      setQueryString(qs => {
        const params = new URLSearchParams(qs)

        if (!range?.from && !range?.to) {
          params.set(name, 'custom')
        } else {
          params.set(name, getUtcDayRangeParam(range))
        }

        return params.toString()
      })
    },
    [name, setQueryString],
  )

  const handleApply = () => {
    if (localRange && !hasCompleteDateRange(localRange)) return

    setDateRange(localRange)
    setOpenDatePicker?.(null)
  }

  const handleClear = () => {
    setLocalRange(undefined)
  }

  const handleCancel = () => {
    setOpenDatePicker?.(null)
    setQueryString(qs => {
      const params = new URLSearchParams(qs)
      // Revert to the first preset option, or remove the filter entirely
      const firstPreset = options[0]?.value
      if (firstPreset) {
        params.set(name, firstPreset)
      } else {
        params.delete(name)
      }
      return params.toString()
    })
  }

  const isIncompleteRange = !!localRange?.from && !localRange.to

  return (
    <>
      <SelectFilter
        name={name}
        options={[customOption].concat(options)}
        setQueryString={setQueryString}
        value={dateRangeName}
      />

      {isCustom && (
        <div className="relative">
          <ShipperPopup
            open={isDatePickerOpen}
            onOpenChange={nextOpen => {
              if (nextOpen) {
                if (start && end) {
                  setLocalRange(selectedRange)
                }
                setOpenDatePicker?.(name)
              } else {
                setOpenDatePicker?.(null)
              }
            }}
            positionerProps={{
              align: 'center',
            }}
            trigger={
              start && end ? (
                <FvButton
                  icon="calendar-day"
                  className="fa-width-auto text-left bg-white w-full border border-fv-gray p-1 mt-2 mb-1"
                  transform="down-1"
                >
                  <span>
                    {selectedRange.from
                      ? fvDate(selectedRange.from).format(dateFormat)
                      : 'mm/dd/yyyy'}
                    {' - '}
                    {selectedRange.to
                      ? fvDate(selectedRange.to).format(dateFormat)
                      : 'mm/dd/yyyy'}
                  </span>
                </FvButton>
              ) : (
                <span className="w-full inline-block"></span>
              )
            }
          >
            <div>
              <div className="flex items-center mb-2">
                <p className="mb-0 text-sm">
                  {!localRange?.from &&
                    !localRange?.to &&
                    'Please select the first day.'}
                  {localRange?.from &&
                    !localRange?.to &&
                    'Please select the last day.'}
                  {localRange?.from &&
                    localRange?.to &&
                    `Selected from ${localRange.from.toLocaleDateString()} to ${localRange.to.toLocaleDateString()}`}
                </p>
              </div>
              <Pinline className="mb-4" />
              <DayPicker
                mode="range"
                numberOfMonths={1}
                selected={localRange}
                onSelect={range => {
                  if (range && !range.to && range.from) {
                    // Auto-set 'to' to be the same as 'from' if only one day selected
                    setLocalRange({ from: range.from, to: range.from })
                  } else {
                    setLocalRange(range)
                  }
                }}
              />
              <Pinline className="mb-4 mt-3 !border-fv-blue" dashed />
              <div className="flex items-center">
                <FvButton
                  theme="primary"
                  icon="check"
                  onClick={handleApply}
                  disabled={isIncompleteRange}
                >
                  Apply and close
                </FvButton>
                <FvButton icon="times" theme="default" onClick={handleClear}>
                  Clear
                </FvButton>
                <FvButton theme="plain" icon="times" onClick={handleCancel} />
              </div>
            </div>
          </ShipperPopup>
        </div>
      )}
    </>
  )
}
